import { MultiMap, iterateNestedMaps, IMap, ExcludeFromTuple } from "./MultiMap";
import { BagExtra, bagForAllGeneric } from "./Bag2";

/**
 * Eine Bag mit mehreren Keys
 */
export class MultiBag<Keys extends any[]> extends MultiMap<Keys, number> implements BagExtra<Keys> {
  get(keys: Keys): number {
    return super.has(keys) ? (super.get(keys) as number) ?? Number.NaN : 0;
  }

  inc(keys: Keys, amount: number): void {
    const { map, key } = this.ensureAndGetLastMapAndKey(keys, true);
    const oldAmount = this.get(keys);
    const newAmount = oldAmount + amount;
    map.set(key, newAmount);
  }

  dec(keys: Keys, amount: number): void {
    this.inc(keys, -amount);
  }

  incAll(otherBag: MultiBag<Keys>): void {
    otherBag.forEach((amount, keys) => this.inc(keys, amount));
  }

  decAll(otherBag: MultiBag<Keys>): void {
    otherBag.forEach((amount, keys) => this.dec(keys, amount));
  }

  multiply(value: number): void {
    this.forEach((number, keys) => this.set(keys, number * value));
  }

  add(value: number): void {
    this.forEach((number, keys) => this.set(keys, number + value));
  }

  from(iterable: BagExtra<Keys> | MultiMap<Keys, number> | Set<Keys> | Array<Keys>): this {
    this.clear();
    bagForAllGeneric<Keys>(iterable, (keys, amount) => this.inc(keys, amount));
    return this;
  }

  /**
   * Summe der Werte aller Teilbäume unterhalb von subKeys
   */
  getSumOfSubTree<StartKeys extends Partial<Keys>>(subKeys: StartKeys): number {
    let s = 0;
    const map = this.getMap(subKeys, false);
    iterateNestedMaps<any, number>(map, (value: number, keys: any) => {
      const number = typeof value == "number" ? value : 0;
      s += number;
    });
    return s;
  }

  get [Symbol.toStringTag](): string {
    return "[object MultiBag]";
  }

  getSubBag<StartKeys extends Partial<Keys>>(
    startKeys: StartKeys,
  ): MultiBag<ExcludeFromTuple<Keys, StartKeys>> {
    const { map, key } = this.ensureAndGetLastMapAndKey(startKeys, false);
    return new MultiBag(map);
  }
}
